import { useEffect, useState } from 'react';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { LwButton, LwDrawer, LwStatusPill, type LwStatusTone } from '@/components/lw';
import { getAppraisalFeeHistory, type AppraisalFeeHistoryEntry } from '@/services/appraisalFeeComplianceService';
import { APPRAISAL_FEE_COLUMN_LABEL, appraisalFeeBasisLabel } from '../../../../../supabase/functions/_shared/invoice-fee-display-contract';
import { AWAITING_DISCLOSURE, chainSourceFromHistory, collapseFeeHistory, feeAuditChain, feeOutcomeSummary, feeTime, type FeeChainSource, type FeeHistoryGroup } from './feeAuditChain';
import './appraisal.css';

/** A saved finding from the report or Loan Center that identifies one invoice on one loan. */
export interface FeeHistorySubject extends FeeChainSource {
  id: string;
  loanId?: string;
  loanNumber: string;
  borrowerName?: string | null;
  documentTitle: string;
  invoiceFolder?: string | null;
  attachmentId?: string;
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const amount = (cents: number | null) => cents === null ? 'Not available' : money.format(cents / 100);
const OUTCOME_PILL: Record<AppraisalFeeHistoryEntry['outcome'], { label: string; tone: LwStatusTone }> = {
  overage: { label: 'Over recorded fee', tone: 'destructive' },
  review: { label: 'Needs review', tone: 'warning' },
  passed: { label: 'Within fee', tone: 'success' },
  excluded: { label: 'Not compared', tone: 'neutral' },
  duplicate: { label: 'Duplicate copy', tone: 'neutral' },
  superseded: { label: 'Replaced', tone: 'neutral' },
  waiting: { label: 'Not checked', tone: 'neutral' }
};

function readerLabel(version: string | null): string | null {
  const match = version?.match(/v(\d+)$/);
  return match ? `Reader v${match[1]}` : null;
}

function HistoryEntry({ group }: { group: FeeHistoryGroup }) {
  const entry = group.newest;
  const pill = entry.reasonCode === AWAITING_DISCLOSURE
    ? { label: 'Awaiting disclosure', tone: 'neutral' as const } : OUTCOME_PILL[entry.outcome];
  const rechecked = entry.checkedAt && Date.parse(entry.checkedAt) - Date.parse(entry.createdAt) > 60000;
  const meta = [
    group.manual ? 'Manual rerun' : 'Scheduled check',
    appraisalFeeBasisLabel(entry.ruleVersion, entry.feeSource),
    readerLabel(entry.readerVersion),
    ...(entry.notes || []),
    entry.identityNote,
    entry.associationStatus === 'matched' ? 'Matched to an appraisal report' : entry.associationStatus ? 'No appraisal match yet' : null,
    entry.orderNumber ? `Order ${entry.orderNumber}` : null,
    entry.invoiceAmountStatus === 'extracted' ? 'Amount extracted, not verified' : null,
    entry.renameState === 'confirmed' ? `Named ${entry.renameTitle || 'in Encompass'}` : entry.renameState === 'review' ? 'Naming needs review' : null,
    entry.cures && (entry.cures.requiredCents !== null || entry.cures.appliedCents !== null)
      ? `Loan cure required ${amount(entry.cures.requiredCents)}, applied ${amount(entry.cures.appliedCents)}` : null,
    entry.attachmentActive ? null : 'This file is no longer in the invoice folder'
  ].filter((item): item is string => Boolean(item));
  const details = <>{meta.join(' · ')} · <span>{feeOutcomeSummary(entry)}</span>
    {group.count > 1 && <> · <span className="fee-history__folded">Checked {group.count} times since {feeTime(group.oldest.createdAt)}, same result</span></>}
    {group.count === 1 && rechecked && <> · Rechecked {feeTime(entry.checkedAt)}</>}
  </>;
  const detailTitle = [...meta, feeOutcomeSummary(entry), group.count > 1 ? `Checked ${group.count} times since ${feeTime(group.oldest.createdAt)}, same result` : null].filter(Boolean).join(' · ');
  return (
    <li className="fee-history__entry" data-fee-history-entry={entry.id}>
      <time title={feeTime(entry.checkedAt || entry.createdAt)}>{feeTime(entry.checkedAt || entry.createdAt)}</time>
      <span className="fee-history-result"><LwStatusPill label={pill.label} tone={pill.tone} />{group.count > 1 && <span className="fee-history-fold-count">×{group.count}</span>}</span>
      {[entry.invoiceCents, entry.feeCents, entry.outcome === 'overage' ? entry.excessCents : null].map((value, index) =>
        <span key={index} className="fee-history-money" data-excess={index === 2 && value !== null && value !== 0} title={value === null ? 'Not available' : undefined}>{value === null ? '—' : amount(value)}</span>)}
      <span className="fee-history__meta" title={detailTitle}>{details}</span>
    </li>
  );
}

/**
 * Right-side drawer: the latest run as a chain, then every saved version as a ledger.
 * The parent passes its newest row each render; the drawer reloads history whenever
 * that row changes, keeps polling while a run is busy, and follows the newest saved
 * version when the row it opened with is gone or older than the ledger.
 */
export function AppraisalFeeHistoryDrawer({ subject, onClose, onOpenAppraisal, onOpenInvoice, onRerun, invoiceBusy = false, appraisalBusy = false, appraisalUnavailable = false, rerunDisabled = false }: {
  subject: FeeHistorySubject; onClose: () => void;
  onOpenAppraisal?: () => void; onOpenInvoice?: () => void; onRerun?: () => void;
  invoiceBusy?: boolean; appraisalBusy?: boolean; appraisalUnavailable?: boolean; rerunDisabled?: boolean;
}) {
  const [entries, setEntries] = useState<AppraisalFeeHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const loanId = subject.loanId;
  const subjectKey = `${subject.id}|${subject.pending ? 1 : 0}|${subject.freshState || ''}|${subject.checkedAt || ''}|${subject.renameState || ''}`;
  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (!loanId) {
      setEntries(null);
      setError('Reload the report to view history for this loan.');
      return undefined;
    }
    getAppraisalFeeHistory(loanId).then(rows => {
      if (!cancelled) {
        setEntries(rows);
      }
    }).catch((cause: unknown) => {
      if (!cancelled) {
        setError(cause instanceof Error ? cause.message : 'Audit history could not be loaded.');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loanId, attempt, subjectKey]);
  const current = entries?.find(entry => entry.current && entry.attachmentId === subject.attachmentId) || null;
  const subjectCheck = Date.parse(subject.checkedAt || subject.evaluatedAt || '') || 0;
  const ledgerCheck = current ? Date.parse(current.checkedAt || current.createdAt) || 0 : 0;
  const chain = feeAuditChain(current && ledgerCheck > subjectCheck ? chainSourceFromHistory(current) : subject);
  useEffect(() => {
    if (!chain.busy || !loanId) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        setAttempt(value => value + 1);
      }
    }, 10000);
    return () => window.clearInterval(timer);
  }, [chain.busy, loanId]);
  const groups = entries ? collapseFeeHistory(entries, subject.attachmentId) : [];
  const runLabel = chain.kind === 'manual' ? 'Manual rerun' : chain.kind === 'scheduled' ? 'Scheduled check' : 'No check yet';
  return (
    <LwDrawer className="fee-history-drawer" onClose={onClose} label={`Audit history for ${subject.documentTitle}`}>
      <div className="fee-history-header">
        <div className="dw-head">
          <div className="dw-id">
            <div className="dw-name"><span className="fee-history-eyebrow">Audit history</span><span>{subject.documentTitle}</span></div>
            <div className="dw-sub">{`${subject.loanNumber}${subject.borrowerName ? ` for ${subject.borrowerName}` : ''}${subject.invoiceFolder ? ` · ${subject.invoiceFolder}` : ''}`}</div>
          </div>
          <LwButton variant="sm" className="fee-history-close" onClick={onClose}>Close</LwButton>
        </div>
      </div>
      <section className="fee-history-latest" aria-labelledby="fee-history-latest">
        <div className="fee-history-latest-top">
          <h3 id="fee-history-latest" className="fee-history-heading">Latest run</h3>
          <ol className="fee-history-steps" aria-label={`Audit steps for ${subject.documentTitle}`}>
            {chain.links.map(link => <li key={link.key} data-fee-chain-link={link.key} data-state={link.state} title={link.detail}>
              <span className="fee-history-step-dot" aria-hidden="true" /><span>{link.label}</span><span className="sr-only">{link.detail}</span>
            </li>)}
          </ol>
          <div className="fee-chain-run"><span>{runLabel}{chain.startedAt && <> <strong>{feeTime(chain.startedAt)}</strong></>}</span>{(chain.busy || chain.finishedAt) && <span aria-hidden="true">·</span>}<span role="status">{chain.busy ? 'Running now' : chain.finishedAt ? `Finished ${feeTime(chain.finishedAt)}` : ''}</span></div>
        </div>
        <p className="fee-chain-summary">{chain.summary}</p>
      </section>
      <div className="dw-body fee-history-body" tabIndex={0} role="region" aria-label="Invoice audit history">
        <section aria-labelledby="fee-history-list">
          <h3 id="fee-history-list" className="fee-history-heading">History</h3>
          {error && (
            <div role="alert" className="space-y-2">
              <p className="fee-history-empty">{error}</p>
              {loanId && <LwButton variant="sm" onClick={() => setAttempt(value => value + 1)}>Try again</LwButton>}
            </div>
          )}
          {!error && !entries && (
            <div className="fee-history-skeleton" role="status" aria-label="Loading audit history">
              <span /><span /><span />
            </div>
          )}
          {entries && groups.length === 0 && <p className="fee-history-empty">No saved checks for this invoice yet.</p>}
          {groups.length > 0 && (
            <div className="fee-history-ledger" tabIndex={0} role="region" aria-label="Scroll saved audit versions">
              <div className="fee-history-columns" aria-hidden="true"><span>Checked</span><span>Result</span><span>Invoice</span><span>{APPRAISAL_FEE_COLUMN_LABEL}</span><span>Diff</span><span>Run detail</span></div>
              <ol className="fee-history" aria-label="Saved audit versions, newest first">
                {groups.map(group => <HistoryEntry key={group.id} group={group} />)}
              </ol>
            </div>
          )}
        </section>
      </div>
      <div className="dw-foot fee-history-footer"><span>{groups.reduce((sum, group) => sum + group.count, 0)} saved checks · identical checks folded</span><div>
        {onOpenAppraisal && <LwButton variant="sm" aria-busy={appraisalBusy} aria-disabled={appraisalUnavailable} title={appraisalUnavailable ? 'No appraisal report on this loan yet' : undefined} onClick={() => {
          if (!appraisalUnavailable && !appraisalBusy) {
            onOpenAppraisal();
          }
        }}><span aria-hidden="true">{appraisalBusy ? <Loader2 className="animate-spin" size={14} /> : <ArrowUpRight size={14} />}</span>Open appraisal</LwButton>}
        {onOpenInvoice && <LwButton variant="sm" aria-label={`Open invoice for ${subject.loanNumber}`} aria-busy={invoiceBusy} onClick={onOpenInvoice}>Open invoice</LwButton>}
        {onRerun && <LwButton variant="sm-solid" aria-disabled={rerunDisabled} title={rerunDisabled ? 'Rerun in progress' : undefined} onClick={() => {
          if (!rerunDisabled) {
            onClose(); onRerun();
          }
        }}>Rerun audit</LwButton>}
      </div></div>
    </LwDrawer>
  );
}
